import {
    GridRow,
    GridCell,
    TextButton,
    Breadcrumb,
    List,
    ListItem,
    TabBar,
    Tab,
    Typography,
    TextField,
    ExposedDropdownMenu,
    OverflowMenu,
    Dialog,
    RadioButtonGroup,
    RadioButton,
    Checkbox
} from "@episerver/ui-framework";
import React, { Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import AddressList from "../../AddressManager/components/AddressList";
import { Address, InView } from "../../AddressManager/model/address";
import {
    initialAddressFormState,
    onOpenDialog as onOpenAddressDialog,
} from "../../AddressManager/reducers/addressFormReducer";
import { fetchEnumsByType } from "../../EnumManager/reducers/asyncThunks";
import {
    enumFormTitle,
    initialEnumFormState,
    onOpenDialog as onOpenEnumDialog,
} from "../../EnumManager/reducers/enumFormReducer";
import { enumType } from "../../EnumManager/reducers/enumManagerReducer";
import { Organization } from "../../OrganizationManager/model/organization";
import { Contact, ContactTab, EnumData, KeyValueOption, ContactType, ContactsType, UserExtendedProps,  AccountExtendedProps} from "../model/contact";
import {
    fetchAddressesByContactId,
    fetchContactById,
    fetchCurrencies,
    fetchLanguages,
    fetchOrganizations,
    updateContact,
    fetchUserByContactId,
    createCustomerAccount
} from "../reducers/asyncThunks";
import {
    changeTitle,
    setParentId,
    contactSelector,
    contactTitle,
    currenciesSelector,
    customerGroupsSelector,
    languagesSelector,
    organizationsSelector,
    breadcrumbItemsSelector,
    updateBreadcrumb,
    titleSelector,
    activeTabSelector,
    changeActiveTab,
    reset as resetData,
    onChangeProperty,
    onAddAcountDialog,
    customerAccountSelector,
    onChangeAccount,
    errorSelector,
    setCustomerGroupId
} from "../reducers/contactDetailReducer";
import { useAppDispatch, useAppSelector } from "../../hooks";
import ContactNoteManager from "../../ContactNoteManager/components/ContactNoteManager";
import { useState } from "react";
import { Property } from "../../../businessFoundation/MetaClassData/MetaClassDataList/MetaClassDataModel";
import { Controller, useForm } from "react-hook-form";
import { unwrapResult } from "@reduxjs/toolkit";
import ExtendedProperty from "../../ExtendedProperty/ExtendedProperty";
import { Snackbar, SnackbarAction } from "@rmwc/snackbar";
import AddParentOrganization from "./AddParentOrganization";
import AddAssignmentsToUser from "./AddAssignmentsToUser";
import UserAccountAssignments from "./UserAccountAssignments";

export const limitString = (val: string, num: number) => {
    if (val && val.length > num) {
        return val.substring(0, num) + "...";
    } else {
        return val;
    }
};

interface ContactDetailProps {
    type: ContactType;
}

const ContactDetail = (props: ContactDetailProps) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { contactId } = useParams<{ contactId: string }>();
    const title = useSelector(titleSelector);
    const initialContact = useSelector(contactSelector);
    const languages = useSelector(languagesSelector);
    const customerGroups = useSelector(customerGroupsSelector);
    const currencies = useSelector(currenciesSelector);
    const organizations = useSelector(organizationsSelector);
    const breadcrumbItems = useSelector(breadcrumbItemsSelector);
    const activeTab = useSelector(activeTabSelector);
    const customerAccount = useSelector(customerAccountSelector);
    const [invalidUsername, setInvalidUsername] = useState(false);
    const [invalidUsernameMessage, setInvalidUsernameMessage] = useState("");
    const [invalidEmail, setInvalidEmail] = useState(false);
    const [invalidPassword, setInvalidPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [errorOpen, setErrorOpen] = useState(false);
    const error = useSelector(errorSelector);
    let type = props.type;

    if (type === ContactType.Contact && initialContact.customerGroup !== null)  {
        switch(initialContact.customerGroup) {
            case "User": 
            case "AccountAdmin":
                type = ContactType.User;
                break;
            case "Account":
                type = ContactType.Account;
                break;
            default: break;
        }
    }

    const allowedExtendedPropsNames = type === ContactType.Account ? AccountExtendedProps : UserExtendedProps;

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        setError,
        clearErrors,
        trigger,
        reset,
        formState: { errors },
    } = useForm<Contact>({
        mode: "onChange",
        defaultValues: initialContact,
    });

    const values = watch();

    useEffect(() => {
        reset(initialContact);
    }, [initialContact]);

    const setCustomerGroupIdData = (contact) => {
        if (contact && (contact.customerGroupId === "0" || contact.customerGroupId === null))
        {
            switch(type) {
                case ContactType.Account: 
                case ContactType.User:
                    dispatch(setCustomerGroupId(type.toString()))
                    break;
                case ContactType.Contact: 
                default:  
            }
        }
    }

    const initComponent = async () => {
        dispatch(fetchCurrencies());
        dispatch(fetchLanguages());
        dispatch(fetchEnumsByType(enumType.ContactGroup));
        dispatch(fetchOrganizations());
        dispatch(changeActiveTab(0));
        dispatch(fetchContactById(contactId))
        .then((data) => {
            data && setCustomerGroupIdData(data.payload);
        });
        dispatch(updateBreadcrumb( {
            title: ContactsType[type],
            level: 1,
            link: `#/${ContactsType[type]}`,
        }));
        if (contactId !== undefined) {
            dispatch(changeTitle(contactTitle.Edit + ContactType[type]));
            dispatch(fetchAddressesByContactId(contactId));
        } else {
            dispatch(changeTitle(contactTitle.Add + ContactType[type]));
        }
        dispatch(fetchUserByContactId(contactId)); 
    };

    const onSubmit = async (contact: Contact) => {
        await dispatch(
            updateContact({
                ...contact,
                primaryKeyId: contactId ? contact.primaryKeyId : null,
                ownerId: contact.ownerId === "0" ? null : contact.ownerId,
                customerGroupId: contact.customerGroupId === "0" ? null : contact.customerGroupId,
                preferredShippingAddressId:
                    contact.preferredShippingAddressId === "0" || !contactId
                        ? null
                        : contact.preferredShippingAddressId,
                preferredBillingAddressId:
                    contact.preferredBillingAddressId === "0" || !contactId ? null : contact.preferredBillingAddressId,
                preferredCurrencyId: contact.preferredCurrencyId === "0" ? null : contact.preferredCurrencyId,
                preferredLanguageId: contact.preferredLanguageId === "0" ? null : contact.preferredLanguageId,
                extendedProperties:
                    contact.extendedProperties?.map((prop: Property) => ({
                        name: prop.name,
                        value: prop.value,
                    })) ?? [],
                addresses: contact.addresses?.map((address: Address) => {
                    return {
                        ...address,
                        extendedProperties:
                            address.extendedProperties?.map((prop: Property) => ({
                                name: prop.name,
                                value: prop.value,
                            })) ?? [],
                    };
                }),
            })
        )
            .then(unwrapResult)
            .then(() => {
                history.push(`/${ContactsType[type]}`);
            })
            .catch(() => {
                setErrorOpen(true);
            });
    };

    const onChangeAccountField = (e: any) => {
        e.persist();
        let { name, value } = e.target;
        const emailRegex =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        switch (name) {
            case "password":
                let passRegexLength = new RegExp("^(?=.{6,})");
                let passRegexLower = new RegExp("^(?=.*[a-z])");
                let passRegexUpper = new RegExp("^(?=.*[A-Z])");
                let passRegexNumber = new RegExp("^(?=.*[0-9])");
                let passRegexSymbol = new RegExp("^(?=.*[!@#$%^&*])");

                if (!passRegexLength.test(value)) {
                    setInvalidPassword(true);
                    setErrorMessage("Passwords must be at least 6 characters.");
                } else if (!passRegexLower.test(value)) {
                    setInvalidPassword(true);
                    setErrorMessage("Passwords must have at least one lowercase ('a'-'z').");
                } else if (!passRegexUpper.test(value)) {
                    setInvalidPassword(true);
                    setErrorMessage("Passwords must have at least one uppercase ('A'-'Z').");
                } else if (!passRegexNumber.test(value)) {
                    setInvalidPassword(true);
                    setErrorMessage("Passwords must have at least one non alphanumeric character.");
                } else if (!passRegexSymbol.test(value)) {
                    setInvalidPassword(true);
                    setErrorMessage("Passwords must use at least 1 different characters.");
                } else {
                    setInvalidPassword(false);
                    setErrorMessage("");
                }
                break;
            case "username":
                if (!emailRegex.test(value)) {
                    setInvalidUsername(true);
                    setInvalidUsernameMessage("Username should be an email address.");
                } else {
                    setInvalidUsername(false);
                    setInvalidUsernameMessage("");
                }
                break;
            case "email":
                if (value === "") {
                    setInvalidEmail(true);
                } else {
                    if (emailRegex.test(value)) {
                        setInvalidEmail(false);
                    } else {
                        setInvalidEmail(true);
                    }
                }
                break;
            default:
                break;
        }

        dispatch(onChangeAccount({ name: name, value: value }));
    };

    const onChangeRadioButton = (e: any) => {
        e.persist();
        let { name, value } = e.target;
        if (value === "true") {
            value = true;
        } else {
            value = false;
        }
        dispatch(onChangeAccount({ name: name, value: value }));
    };

    useEffect(() => {
        initComponent();
    }, [contactId]);

    useEffect(() => {
        dispatch(
            updateBreadcrumb({
                level: 2,
                active: true,
                title: initialContact.fullName,
                link: "",
            })
        );
    }, [initialContact.fullName]);

    useEffect(() => {
        return () => {
            dispatch(resetData());
        };
    }, []);

    useEffect(() => {
        dispatch(onChangeProperty(values));
    }, [activeTab != ContactTab.Details]);

    const renderUserAccountAssignments = (): React.ReactNode => {
        return (
            <Fragment>
                <AddAssignmentsToUser contactId={contactId ?? ""} type={type}/>
                <UserAccountAssignments contactId={contactId ?? ""} type={type}/>
            </Fragment>
        );
    };

    const renderDetails = (): React.ReactNode => {
        return (
            <Fragment>
                <form
                    autoComplete="off"
                    onSubmit={(e) => {
                        e.preventDefault();
                    }}
                >
                    <GridRow className="grid-row">
                        
                        <GridCell span={6}>
                            <div className="control">
                                <Controller
                                    name="fullName"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: "The full name is required",
                                        },
                                    }}
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            id="fullName"
                                            autoFocus
                                            maxLength={200}
                                            outlined
                                            label="Full Name"
                                            name="fullName"
                                            helperText={errors.fullName?.message ?? ""}
                                            invalid={!!errors.fullName?.message}
                                            value={value ?? ""}
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            </div>
                        </GridCell>
                        {type !== ContactType.Account && 
                        <GridCell span={6}>
                            <div className="control">
                                <Controller
                                    name="middleName"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            id="middleName"
                                            maxLength={64}
                                            outlined
                                            label="Middle Name"
                                            name="middleName"
                                            value={value ?? ""}
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            </div>
                        </GridCell>}
                        {type !== ContactType.Account && 
                        <GridCell span={6}>
                            <div className="control">
                                <Controller
                                    name="firstName"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            id="firstName"
                                            maxLength={64}
                                            outlined
                                            label="First Name"
                                            name="firstName"
                                            helperText={errors.firstName?.message ?? ""}
                                            invalid={!!errors.firstName?.message}
                                            value={value ?? ""}
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            </div>
                        </GridCell>}
                        {type !== ContactType.Account && 
                        <GridCell span={6}>
                            <div className="control">
                                <Controller
                                    name="lastName"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: "The last name is required",
                                        },
                                    }}
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            id="lastName"
                                            maxLength={64}
                                            outlined
                                            label="Last Name"
                                            name="lastName"
                                            helperText={errors.lastName?.message ?? ""}
                                            invalid={!!errors.lastName?.message}
                                            value={value ?? ""}
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            </div>
                        </GridCell>}
                        {type !== ContactType.Account && 
                            <GridCell span={6}>
                                <div className="control">
                                    <Controller
                                        name="email"
                                        control={control}
                                        rules={{
                                            required: {
                                                value: true,
                                                message: "The email is required",
                                            },
                                        }}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                id="email"
                                                maxLength={254}
                                                outlined
                                                label="Email"
                                                name="email"
                                                helperText={errors.email?.message ?? ""}
                                                invalid={!!errors.email?.message}
                                                value={value ?? ""}
                                                onChange={onChange}
                                            />
                                        )}
                                    />
                                </div>
                            </GridCell>
                        }
                        {props.type !== ContactType.Account && <GridCell span={6}>
                            <div className="control">
                                <Controller
                                    name="registrationSource"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            outlined
                                            maxLength={100}
                                            label="Registration Source"
                                            name="registrationSource"
                                            value={value ?? ""}
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            </div>
                        </GridCell>}
                        <GridCell span={6}>  
                         <AddParentOrganization
                                setParentId={(parentId: string | null) => {
                                    dispatch(onChangeProperty(values));
                                    if (parentId !== values.ownerId) {
                                        dispatch(setParentId(parentId));
                                  }
                             }}
                             contactId={contactId} />
                        </GridCell>
                       
                            <GridCell span={6}>
                                <div className="float-left dropdown-custom">
                                    <ExposedDropdownMenu
                                        className="field-custom"
                                        label="Customer Group"
                                        options={customerGroups.map((customerGroup: EnumData) => ({
                                            label: limitString(customerGroup.name, 40),
                                            value: customerGroup.id.toString(),
                                        }))}
                                        value={
                                            values.customerGroupId !== null &&
                                            customerGroups.filter((x) => x.id.toString() == values.customerGroupId).length >
                                                0
                                                ? values.customerGroupId.toString()
                                                : "0"
                                        }
                                        onValueChange={(val: string) => {
                                            setValue("customerGroupId", val);
                                        }}
                                    />
                                </div>
                            </GridCell>
                         
                    <GridCell span={6}>
                    {props.type !== ContactType.Account && <div className="control">
                                 <label data-oui-component="true" className="oui-label">
                                    Accept Marketing Email
                                </label>
                                <Controller
                                    name="acceptMarketingEmail"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Checkbox
                                            id="acceptMarketingEmail"
                                            maxLength={64} 
                                            name="acceptMarketingEmail"
                                            checked={value ?? false}
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            </div>}
                    
                    </GridCell>

                    
                        <Fragment>
                            {values.extendedProperties?.map((property: Property, index: number) => {
                                return (allowedExtendedPropsNames.includes(property.name) &&
                                    <GridCell span={6} key={index}>
                                        <div className="control">
                                            <ExtendedProperty
                                                index={index}
                                                className="Contact"
                                                property={property}
                                                clearErrors={clearErrors}
                                                control={control}
                                                errors={errors}
                                                setError={setError}
                                                setValue={setValue}
                                                trigger={trigger}
                                                key={index}
                                            />
                                        </div>
                                    </GridCell>
                                );
                            })}
                        </Fragment>
                        <GridCell
                            span={6}
                            className={contactId === undefined || !!!initialContact.lastOrder ? "hidden" : ""}
                        >
                         <label data-oui-component="true" className="oui-label">Last Order Date</label>
                         <input style={{border: "none", marginLeft: "1rem"}} type="datetime-local" disabled value={initialContact.lastOrder?.slice(0, 16)}/>

                       
                        </GridCell>
                        <GridCell span={6} className={props.type !== ContactType.Account ? "hidden" : ""}>
                            <label data-oui-component="true" className="oui-label">Created</label>
                            <input style={{border: "none", marginLeft: "1rem"}} type="datetime-local" disabled value={initialContact.created?.slice(0, 16)}/>
                        </GridCell>
                        <GridCell span={6}  className={props.type !== ContactType.Account ? "hidden" : ""}>
                            <label data-oui-component="true" className="oui-label">Modified</label>
                            <input style={{border: "none", marginLeft: "1rem"}} type="datetime-local" disabled value={initialContact.modified?.slice(0, 16)}/>
                        </GridCell>
                        <GridCell span={6}  className={props.type !== ContactType.Account ? "hidden" : ""}>
                            <label data-oui-component="true" className="oui-label">ID</label>
                            <label style={{border: "none", marginLeft: "1rem"}}>{initialContact.primaryKeyId ?? ""}</label>
                        </GridCell>
                        <GridCell span={12} className={type === ContactType.Account ? "hidden" : ""}>
                            <div className="control">
                                <TextField
                                    outlined
                                    className="account-field"
                                    label="Account"
                                    name="Account"
                                    value={!!customerAccount.username ? customerAccount.username : "Account not found"}
                                    readOnly
                                />
                            </div>
                        </GridCell>
                    </GridRow>
                </form>
                <Dialog
                    open={customerAccount.open}
                    className="medium-dialog"
                    title="Add new Account"
                    dismissLabel="Cancel"
                    confirmLabel="Save"
                    enableConfirm={
                        !invalidUsername && !invalidEmail && !invalidPassword && customerAccount.password !== ""
                    }
                    onInteraction={(evt) => {
                        if (evt.detail.action === "confirm") {
                            dispatch(createCustomerAccount({ ...customerAccount, contactId: contactId }));
                        }
                        dispatch(
                            onAddAcountDialog({
                                open: false,
                                username: "",
                                password: "",
                                description: "",
                                email: "",
                                approved: false,
                            })
                        );
                    }}
                >
                    <GridRow className="parameter">
                        <GridCell span={12}>
                            <TextField
                                className="control"
                                outlined
                                label="Username"
                                name="username"
                                value={customerAccount.username}
                                invalid={invalidUsername}
                                helperText={customerAccount.username === "" ? "* Required" : invalidUsernameMessage}
                                onChange={(e: any) => {
                                    onChangeAccountField(e);
                                }}
                            />
                        </GridCell>
                        <GridCell span={12}>
                            <TextField
                                className="control"
                                outlined
                                label="Email"
                                name="email"
                                value={customerAccount.email}
                                invalid={invalidEmail}
                                helperText={invalidEmail ? "Must use a valid email address." : ""}
                                onChange={(e: any) => {
                                    onChangeAccountField(e);
                                }}
                            />
                        </GridCell>
                        <GridCell span={12}>
                            <div className="control">
                                <TextField
                                    className={"control"}
                                    outlined
                                    label="Password"
                                    name="password"
                                    type="password"
                                    helperText={customerAccount.password === "" ? "* Required" : errorMessage}
                                    invalid={invalidPassword}
                                    value={customerAccount.password}
                                    onChange={(e: any) => {
                                        onChangeAccountField(e);
                                    }}
                                />
                            </div>
                        </GridCell>
                        <GridCell span={12}>
                            <div className="control">
                                <TextField
                                    className="control"
                                    outlined
                                    label="Description"
                                    name="description"
                                    value={customerAccount.description}
                                    onChange={(e: any) => {
                                        onChangeAccountField(e);
                                    }}
                                />
                            </div>
                        </GridCell>
                        <GridCell span={12}>
                            <div className="control">
                                <RadioButtonGroup header="Approved" name="approved">
                                    <RadioButton
                                        value="true"
                                        checked={customerAccount.approved}
                                        onChange={(e: any) => {
                                            onChangeRadioButton(e);
                                        }}
                                    >
                                        Yes
                                    </RadioButton>
                                    <RadioButton
                                        value="false"
                                        checked={!customerAccount.approved}
                                        onChange={(e: any) => {
                                            onChangeRadioButton(e);
                                        }}
                                    >
                                        No
                                    </RadioButton>
                                </RadioButtonGroup>
                            </div>
                        </GridCell>
                    </GridRow>
                </Dialog>
            </Fragment>
        );
    };

    const renderAddresses = (): React.ReactNode => {
        return <AddressList inTab inView={InView.Contact} viewId={contactId} />;
    };

    const renderContactNotes = (): React.ReactNode => {
        return <ContactNoteManager />;
    };

    return (
        <Fragment>
            {!contactId && (
                <Fragment>
                    <Typography use="headline1">{title}</Typography>
                    <br />
                </Fragment>
            )}
            {contactId && (
                <React.Fragment>
                    <Breadcrumb items={breadcrumbItems} />
                    <br></br>
                </React.Fragment>
            )}
            <TabBar
                activeTabIndex={activeTab}
                onActivate={(e) => {
                    dispatch(changeActiveTab(e.detail.index));
                }}
            >
                {contactId && (
                    <Fragment>
                        <Tab>Details</Tab>
                        <Tab>Addresses</Tab>
                        <Tab>Contact notes</Tab>
                        {type === ContactType.User && <Tab>Account Assignments</Tab>}
                        {type === ContactType.Account && <Tab>User Assignments</Tab>}
                    </Fragment>
                )}
            </TabBar>
            <br />
            {<div className={activeTab === ContactTab.Details ? "" : "hidden"}> 
               {renderDetails()}
            </div>}
            {contactId && activeTab === ContactTab.Addresses && renderAddresses()}
            {contactId && activeTab === ContactTab.ContactNote && renderContactNotes()}
            {contactId && activeTab === ContactTab.AccountAssignments && renderUserAccountAssignments()}
            <br />
            <div className="edit-form-buttons right-buttons">
                <TextButton onClick={() => history.push(`/${ContactsType[type]}`)}>Cancel</TextButton>
                <TextButton contained onClick={() => handleSubmit(onSubmit)()}>
                    Save
                </TextButton>
            </div>
            {errorOpen && 
                <Snackbar
                open={errorOpen}
                message={
                    <Typography tag="p" use={"body1"}>
                        {error}
                    </Typography>
                }
                timeout={-1}
                dismissesOnAction
                onClose={() => {
                    setErrorOpen(false);
                }}
                action={[<SnackbarAction label="Close" />]}
            ></Snackbar>
            }
            
        </Fragment>
    );
};

export default ContactDetail;
